// Copyright 2026. WebPros International GmbH. All rights reserved.

import React from 'react';
import { RootState } from 'admin/core/store';
import { connect } from 'react-redux';
import { OnboardingStep } from 'common/api/resources/Onboarding';
import { OnboardingStepWelcome } from 'admin/onboarding/components/OnboardingStepWelcome';
import OnboardingStepActivateLicense from 'admin/onboarding/components/OnboardingStepActivateLicense';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import { IComputeResourceResponse } from 'common/api/resources/ComputeResource';
import OnboardingStepAddComputeResource from 'admin/onboarding/components/OnboardingStepAddComputeResource';
import OnboardingStepConnectComputeResource from 'admin/onboarding/components/OnboardingStepConnectComputeResource';
import OnboardingStepAddIpBlock from 'admin/onboarding/components/OnboardingStepAddIpBlock';
import { setIpBlockItem } from 'admin/ipBlock/actions';
import { useOnboarding } from 'admin/onboarding/containers/OnboardingButtonWithDialog';
import OnboardingStepAddVmPlan from 'admin/onboarding/components/OnboardingStepAddVmPlan';
import OnboardingStepMailSettings from 'admin/onboarding/components/OnboardingStepMailSettings';
import { OnboardingStepCreateFirstVm } from 'admin/onboarding/components/OnboardingStepCreateFirstVm';
import { track } from 'common/analytics/mixpanel';
import { ANALYTICS_EVENTS } from 'common/analytics/constants';

export interface IOnboardingContentProps {}

export type OnboardingContentProps = IOnboardingContentProps
    & ReturnType<typeof mapStateToProps>
    & ReturnType<typeof mapDispatchToProps>;

export const OnboardingContent: React.FC<OnboardingContentProps> = ({
    computeResource,
    locations,
}) => {
    const { step: currentStep, updateOnboardingState, close } = useOnboarding();

    const computeResourceItem = React.useMemo<IComputeResourceResponse>(() => ({
        ...computeResource,
        locations: locations.data,
    }), [computeResource, locations.data]);

    React.useEffect(() => {
        const trackStepVisit = () => {
            switch (currentStep) {
            case OnboardingStep.INITIAL:
            case OnboardingStep.WELCOME:
                track(ANALYTICS_EVENTS.ONBOARDING.WELCOME.VISIT);
                break;
            case OnboardingStep.ACTIVATE_LICENSE:
                track(ANALYTICS_EVENTS.ONBOARDING.ACTIVATE_LICENSE.VISIT);
                break;
            case OnboardingStep.ADD_COMPUTE_RESOURCE:
                track(ANALYTICS_EVENTS.ONBOARDING.ADD_COMPUTE_RESOURCE.VISIT);
                break;
            case OnboardingStep.CONNECT_COMPUTE_RESOURCE:
                track(ANALYTICS_EVENTS.ONBOARDING.CONNECT_COMPUTE_RESOURCE.VISIT);
                break;
            case OnboardingStep.ADD_IP_BLOCK:
                track(ANALYTICS_EVENTS.ONBOARDING.ADD_IP_BLOCK.VISIT);
                break;
            case OnboardingStep.ADD_VM_PLAN:
                track(ANALYTICS_EVENTS.ONBOARDING.ADD_VM_PLAN.VISIT);
                break;
            case OnboardingStep.MAIL_SETTINGS:
                track(ANALYTICS_EVENTS.ONBOARDING.MAIL_SETTINGS.VISIT);
                break;
            case OnboardingStep.CREATE_FIRST_VM:
                track(ANALYTICS_EVENTS.ONBOARDING.CREATE_FIRST_VM.VISIT);
                break;
            case OnboardingStep.DONE:
                track(ANALYTICS_EVENTS.ONBOARDING.DONE_CLICKED);
                break;
            case OnboardingStep.SKIP:
                track(ANALYTICS_EVENTS.ONBOARDING.SKIP_CLICKED);
                break;
            }
        };

        trackStepVisit();
    }, [currentStep]);

    switch (currentStep) {
    case OnboardingStep.INITIAL:
    case OnboardingStep.WELCOME:
        return (
            <OnboardingStepWelcome
                onStart={async () => {
                    track(ANALYTICS_EVENTS.ONBOARDING.WELCOME.START_CLICKED);
                    await updateOnboardingState(OnboardingStep.ACTIVATE_LICENSE);
                }}
            />
        );
    case OnboardingStep.ACTIVATE_LICENSE:
        return (
            <OnboardingStepActivateLicense
                onNext={() => {
                    updateOnboardingState(OnboardingStep.ADD_COMPUTE_RESOURCE);
                }}
            />
        );
    case OnboardingStep.ADD_COMPUTE_RESOURCE:
        return (
            <OnboardingStepAddComputeResource
                item={computeResourceItem}
                onNext={(computeResourceId) => {
                    updateOnboardingState(OnboardingStep.CONNECT_COMPUTE_RESOURCE, computeResourceId);
                }}
            />
        );
    case OnboardingStep.CONNECT_COMPUTE_RESOURCE:
        return (
            <OnboardingStepConnectComputeResource
                item={computeResourceItem}
                onNext={() => {
                    updateOnboardingState(OnboardingStep.ADD_IP_BLOCK);
                }}
            />
        );
    case OnboardingStep.ADD_IP_BLOCK:
        return (
            <OnboardingStepAddIpBlock
                onNext={() => {
                    updateOnboardingState(OnboardingStep.ADD_VM_PLAN);
                }}
            />
        );
    case OnboardingStep.ADD_VM_PLAN:
        return (
            <OnboardingStepAddVmPlan
                onNext={() => {
                    updateOnboardingState(OnboardingStep.MAIL_SETTINGS);
                }}
            />
        );
    case OnboardingStep.MAIL_SETTINGS:
        return (
            <OnboardingStepMailSettings
                onNext={() => {
                    updateOnboardingState(OnboardingStep.CREATE_FIRST_VM);
                }}
            />
        );
    case OnboardingStep.CREATE_FIRST_VM:
        return (
            <OnboardingStepCreateFirstVm
                onNext={async () => {
                    await updateOnboardingState(OnboardingStep.DONE);
                    close();
                }}
            />
        );
    case OnboardingStep.DONE:
    case OnboardingStep.SKIP:
        return null;
    }

    throw new Error(`Unknown onboarding step: ${currentStep}`);
};

const mapStateToProps = (state: RootState) => ({
    locations: state.location.list,
    computeResource: state.computeResource.item,
    onboarding: state.onboarding,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    setIpBlock: bindActionCreators(setIpBlockItem, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(OnboardingContent);